import { useCallback, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../auth/AuthContext";
import { PricingTier, PricingPrice, User } from "./types";
import { CUSTOMER_PORTAL_SESSION_QUERY } from "./queries";
import {
  fetchSubscriptionPrices,
  getMaximumYearlyDiscountPercent,
} from "./pricingService";
import { checkoutOriginUrl, withEntryQuery } from "../../entryParams";
import { createGraphQLClient } from "../../graphqlClient";
import "./pricing-table.css";

interface PricingTableProps {
  userData: User | null;
  isLoadingUser: boolean;
}

function PricingTable({ userData, isLoadingUser }: PricingTableProps) {
  const { t } = useTranslation();
  const { accessToken } = useAuthContext();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const [pricingData, setPricingData] = useState<PricingTier[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [priceError, setPriceError] = useState<string | null>(null);
  const yearlyDiscountPercent = useMemo(
    () => getMaximumYearlyDiscountPercent(pricingData),
    [pricingData],
  );

  const getGraphQLClient = useCallback(
    () => createGraphQLClient(accessToken),
    [accessToken],
  );

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const loadPrices = async () => {
      setIsLoadingPrices(true);
      setPriceError(null);

      try {
        const client = getGraphQLClient();
        const tiers = await fetchSubscriptionPrices(client, t);
        setPricingData(tiers);
      } catch (error) {
        setPriceError(
          error instanceof Error
            ? error.message
            : t("pricing.error.load"),
        );
      } finally {
        setIsLoadingPrices(false);
      }
    };

    loadPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (
      !userData?.currentSubscription?.subscriptionPlanPrice?.externalPriceId ||
      pricingData.length === 0
    ) {
      return;
    }

    const currentPriceId =
      userData.currentSubscription.subscriptionPlanPrice.externalPriceId;

    for (const tier of pricingData) {
      const matchingPrice = tier.prices.find(
        (p) => p.externalPriceId === currentPriceId,
      );
      if (matchingPrice) {
        setBillingCycle(matchingPrice.type);
        break;
      }
    }
  }, [userData, pricingData]);

  // The order page sits between the price and Stripe: Stripe's own button reads
  // "Abonnieren" and cannot be relabelled, while § 312j Abs. 3 BGB prescribes
  // "zahlungspflichtig bestellen" together with the mandatory information above
  // it (AGB 14.2).
  const handleCheckoutClick = (pricingId: string) => {
    navigate(withEntryQuery(`/order/${encodeURIComponent(pricingId)}`));
  };

  const handleCustomerPortalClick = async () => {
    try {
      const client = getGraphQLClient();

      const variables = {
        input: {
          originUrl: checkoutOriginUrl(),
        },
      };

      const data = await client.request<{
        customerPortalToken: string;
      }>(CUSTOMER_PORTAL_SESSION_QUERY, variables);

      if (data.customerPortalToken) {
        window.location.href = data.customerPortalToken;
      }
    } catch (error) {
      // handle silently
    }
  };

  // A store subscription is managed in the store, so neither the Stripe portal nor a
  // second order may be offered next to it (gg-367).
  const currentProvider = userData?.hasActiveSubscription
    ? userData.currentSubscription?.provider
    : undefined;
  const storeNoticeKey =
    currentProvider === "APP_STORE"
      ? "pricing.store.appStore"
      : currentProvider === "PLAY_STORE"
        ? "pricing.store.playStore"
        : null;

  const getCurrentPrice = (tier: PricingTier): PricingPrice => {
    return tier.prices.find((p) => p.type === billingCycle) || tier.prices[0];
  };

  if (isLoadingPrices) {
    return (
      <div className="pricing-container">
        <div className="pricing-loading">
          <p>{t("pricing.loading")}</p>
        </div>
      </div>
    );
  }

  if (priceError) {
    return (
      <div className="pricing-container">
        <div className="pricing-error">
          <p>{priceError}</p>
          <button
            onClick={() => window.location.reload()}
            className="pricing-button"
          >
            {t("pricing.error.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      <header className="pricing-header">
        <h1 className="pricing-title">{t("pricing.title")}</h1>
        <p className="pricing-subtitle">{t("pricing.subtitle")}</p>

        <div className="billing-toggle">
          <span
            className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}
          >
            {t("pricing.billing.monthly")}
          </span>
          <button
            className="billing-switch"
            onClick={() =>
              setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")
            }
            aria-label="Toggle billing cycle"
          >
            <span
              className={`billing-switch-slider ${billingCycle === "yearly" ? "yearly" : "monthly"}`}
            />
          </button>
          <span
            className={`billing-option ${billingCycle === "yearly" ? "active" : ""}`}
          >
            {t("pricing.billing.yearly")}
            {yearlyDiscountPercent !== null && (
              <span className="billing-badge">
                {t("pricing.billing.badge", {
                  percent: yearlyDiscountPercent,
                })}
              </span>
            )}
          </span>
        </div>
      </header>

      {storeNoticeKey && (
        <div className="pricing-portal-banner">
          <div className="pricing-portal-banner-content">
            <div className="pricing-portal-banner-text">
              <h3 className="pricing-portal-banner-title">
                {t("pricing.store.title")}
              </h3>
              <p className="pricing-portal-banner-description">
                {t(storeNoticeKey)}
              </p>
            </div>
          </div>
        </div>
      )}

      {userData?.hasActiveSubscription && !storeNoticeKey && (
        <div className="pricing-portal-banner">
          <div className="pricing-portal-banner-content">
            <div className="pricing-portal-banner-text">
              <h3 className="pricing-portal-banner-title">
                {t("pricing.portal.title")}
              </h3>
              <p className="pricing-portal-banner-description">
                {t("pricing.portal.description")}
              </p>
            </div>
            <button
              className="pricing-portal-banner-btn"
              onClick={handleCustomerPortalClick}
            >
              {t("pricing.portal.button")}
            </button>
          </div>
        </div>
      )}

      <div className="pricing-grid">
        {pricingData.map((tier) => {
          const currentPrice = getCurrentPrice(tier);
          const isCurrentSubscription =
            userData?.currentSubscription?.subscriptionPlanPrice
              ?.externalPriceId === currentPrice.externalPriceId;
          const hasActiveSubscription =
            userData?.hasActiveSubscription && isCurrentSubscription;
          const hasOtherSubscription =
            userData?.hasActiveSubscription && !isCurrentSubscription;

          return (
            <div
              key={tier.id}
              className={`pricing-card ${tier.highlighted ? "pricing-card-highlighted" : ""} ${hasActiveSubscription ? "pricing-card-active" : ""}`}
            >
              {tier.highlighted && !hasActiveSubscription && (
                <div className="pricing-badge">{t("pricing.card.popular")}</div>
              )}
              {hasActiveSubscription && (
                <div className="pricing-badge pricing-badge-active">
                  {t("pricing.card.currentPlan")}
                </div>
              )}

              <div className="pricing-card-header">
                <h2 className="pricing-tier-name">{tier.name}</h2>
                <p className="pricing-tier-description">{tier.description}</p>
                <div className="pricing-price">
                  <span className="pricing-price-amount">
                    {currentPrice.displayPrice}
                  </span>
                  <span className="pricing-price-subtext">
                    {currentPrice.displaySubtext}
                  </span>
                </div>
              </div>

              <ul className="pricing-features">
                {tier.features.map((feature, index) => (
                  <li
                    key={index}
                    className={`pricing-feature ${feature.included ? "pricing-feature-included" : "pricing-feature-excluded"}`}
                  >
                    <span className="pricing-feature-icon">
                      {feature.included ? "✓" : "✕"}
                    </span>
                    <span className="pricing-feature-text">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`pricing-button ${tier.highlighted ? "pricing-button-highlighted" : ""}`}
                onClick={() =>
                  hasOtherSubscription
                    ? handleCustomerPortalClick()
                    : handleCheckoutClick(currentPrice.externalPriceId)
                }
                disabled={
                  hasActiveSubscription || isLoadingUser || !!storeNoticeKey
                }
              >
                {hasActiveSubscription
                  ? t("pricing.button.active")
                  : storeNoticeKey
                    ? t("pricing.button.store")
                    : hasOtherSubscription
                      ? t("pricing.button.portal")
                      : t("pricing.button.start")}
              </button>
            </div>
          );
        })}
      </div>

    </div>
  );
}

export default PricingTable;
